import env from "@/app/config/env";
import type {
  SupportCaseActivityItemType,
  SupportCaseArticleItemType,
  SupportCaseAttachmentItemType,
  SupportCaseAttachmentRequestType,
  SupportCaseCommentItemType,
  SupportCaseItemType,
  SupportCaseResultType,
  SupportCasesOptionType,
  SupportCasesProductItemType,
} from "@/schemas/support-cases";
import {
  SupportCaseActivityItemSchema,
  SupportCaseArticleItemSchema,
  SupportCaseAttachmentItemSchema,
  SupportCaseCommentItemSchema,
  SupportCaseItemSchema,
  SupportCaseResultSchema,
  SupportCasesOptionSchema,
  SupportCasesProductItemSchema,
} from "@/schemas/support-cases";
import axios, { type AxiosResponse } from "axios";

type SupportCasesFilterValueType = {
  priorityId?: string | number;
  statusId?: string | number;
  productId?: string | number;
  periodId?: string | number;
  contactId?: string | number;
  subject?: string;
  details?: string;
  number?: string;
};

export interface ServiceSupportCasesApiType {
  fetchSupportCases: (
    pageIndex?: number,
    pageSize?: number,
    filterValue?: SupportCasesFilterValueType,
  ) => Promise<SupportCaseResultType>;
  fetchSupportCaseById: (
    number: SupportCaseItemType["Number"],
  ) => Promise<SupportCaseItemType>;
  fetchSupportCaseComments: (
    number: SupportCaseItemType["Number"],
  ) => Promise<SupportCaseCommentItemType[]>;
  fetchSupportCaseActivities: (
    number: SupportCaseItemType["Number"],
  ) => Promise<SupportCaseActivityItemType[]>;
  addSupportCaseComment: (
    number: SupportCaseItemType["Number"],
    data: Pick<SupportCaseCommentItemType, "Subject" | "Text"> & {
      Files?: SupportCaseAttachmentRequestType[];
    },
  ) => Promise<void>;
  closeSupportCase: (number: SupportCaseItemType["Number"]) => Promise<void>;
  fetchFilterPriority: () => Promise<SupportCasesOptionType[]>;
  fetchFilterStatus: () => Promise<SupportCasesOptionType[]>;
  fetchFilterProducts: () => Promise<SupportCasesProductItemType[]>;
  fetchFilterContacts: () => Promise<SupportCasesOptionType[]>;
  fetchFilterPeriod: () => Promise<SupportCasesOptionType[]>;
  fetchSupportCaseAttachments: (
    number: SupportCaseItemType["Number"],
  ) => Promise<SupportCaseAttachmentItemType[]>;
  addSupportCaseAttachment: (
    number: SupportCaseItemType["Number"],
    file: SupportCaseAttachmentRequestType,
  ) => Promise<void>;
  fetchSupportCaseArticles: (
    number: SupportCaseItemType["Number"],
  ) => Promise<SupportCaseArticleItemType[]>;
}

export const serviceSupportCasesApi: ServiceSupportCasesApiType = {
  fetchSupportCases: (pageIndex, pageSize, filterValue) => {
    return axios
      .get(`${env.services.portal}/support/incidents`, {
        params: {
          pageIndex: pageIndex,
          pageSize: pageSize,
          ...(filterValue || {}),
        },
      })
      .then((response: AxiosResponse<unknown>) => {
        return SupportCaseResultSchema.parse(response.data);
      });
  },

  fetchSupportCaseById: (number) => {
    return axios
      .get(`${env.services.portal}/support/incidents/${number}`)
      .then((response: AxiosResponse<unknown>) => {
        return SupportCaseItemSchema.parse(response.data);
      });
  },

  fetchSupportCaseArticles: (number) => {
    return axios
      .get(`${env.services.portal}/support/incidents/${number}/articles`)
      .then((response: AxiosResponse<unknown[]>) => {
        return (
          response.data?.map((item) =>
            SupportCaseArticleItemSchema.parse(item),
          ) || []
        );
      });
  },

  fetchSupportCaseComments: (number) => {
    return axios
      .get(`${env.services.portal}/support/incidents/${number}/comments`)
      .then((response: AxiosResponse<unknown[]>) => {
        return (
          response.data?.map((item) =>
            SupportCaseCommentItemSchema.parse(item),
          ) || []
        );
      });
  },

  fetchSupportCaseActivities: (number) => {
    return axios
      .get(`${env.services.portal}/support/incidents/${number}/activities`)
      .then(
        (response: AxiosResponse<unknown[]>) =>
          response.data?.map((item) =>
            SupportCaseActivityItemSchema.parse(item),
          ) || [],
      );
  },

  addSupportCaseComment: (number, data) => {
    return axios.post(
      `${env.services.portal}/support/incidents/${number}/comments`,
      data,
    );
  },

  closeSupportCase: (number) => {
    return axios.delete(`${env.services.portal}/support/incident/${number}`);
  },

  fetchFilterProducts: () => {
    return axios
      .get(`${env.services.portal}/taxonomy/productfamilies`)
      .then((response: AxiosResponse<unknown[]>) => {
        return response?.data?.map((item) =>
          SupportCasesProductItemSchema.parse(item),
        );
      });
  },

  fetchFilterContacts: () => {
    return axios
      .get(`${env.services.portal}/support/incidents/contacts`)
      .then((response: AxiosResponse<unknown[]>) => {
        return response?.data?.map((item) =>
          SupportCasesOptionSchema.parse(item),
        );
      });
  },

  fetchFilterStatus: () => {
    return Promise.resolve(
      [
        {
          Id: "Active",
          Name: "Active",
        },
        {
          Id: "Closed",
          Name: "Closed",
        },
      ].map((item) => SupportCasesOptionSchema.parse(item)),
    );
  },

  fetchFilterPriority: () => {
    return Promise.resolve(
      [
        {
          Id: "100000005",
          Name: "Minor",
        },
        {
          Id: "100000000",
          Name: "Important",
        },
        {
          Id: "100000004",
          Name: "Urgent",
        },
        {
          Id: "100000002",
          Name: "Critical",
        },
      ].map((item) => SupportCasesOptionSchema.parse(item)),
    );
  },

  fetchFilterPeriod: () => {
    return Promise.resolve(
      [
        {
          Id: "1",
          Name: "Today",
        },
        {
          Id: "2",
          Name: "Last 7 days",
        },
        {
          Id: "3",
          Name: "Last month",
        },
        {
          Id: "4",
          Name: "Last 3 months",
        },
        {
          Id: "5",
          Name: "Last year",
        },
      ].map((item) => SupportCasesOptionSchema.parse(item)),
    );
  },

  fetchSupportCaseAttachments: (number) => {
    return axios
      .get(`${env.services.portal}/support/incidents/${number}/attachments`)
      .then((response: AxiosResponse<unknown[]>) => {
        return (
          response.data?.map((item) =>
            SupportCaseAttachmentItemSchema.parse(item),
          ) || []
        );
      });
  },

  addSupportCaseAttachment: (number, file) => {
    return axios.post(
      `${env.services.portal}/support/incidents/${number}/attachments`,
      file,
    );
  },
};
